import { Box, Grid, Typography } from '@material-ui/core';
import { Button, Card, Tabs, TextField } from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { DateFormat, formatDate, isNotBlank } from '@roc/feature-utils';
import { useDocumentStore } from "@roc/feature-documents";
import { TaskNote, TaskV2 } from '@roc/feature-general-tasks';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    cardWithoutHeader: {
      '& .MuiCardHeader-root': {
        padding: 0,
      },
      '& .MuiCardContent-root': {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(1),
        maxHeight: '80%',
        overflow: 'auto'
      },
    },
    uwDetailsLabel: {
      fontSize: '14px',
      color: '#555',
    },
    uwCommentWrapper: {
      backgroundColor: '#F7F7F7',
      padding: theme.spacing(2),
      borderBottom: '1px solid #e1e1e1',
      marginBottom: 5,
      width: '100%',
    },
    uwCommentText: {
      fontSize: '1.08rem'
    },
  }),
);


interface UnderwriteTheLoanProps {
  taskId: number;
  loanId: number;
  handleClose: () => void;
}

interface TaskNameNotePair {
  taskTitle: string;
  taskNote: TaskNote;
}

export const UnderwriteTheLoan = observer(({ taskId, loanId, handleClose }: UnderwriteTheLoanProps) => {
  const { documentStore } = useDocumentStore();
  const { documentFormStore } = documentStore;
  const { underwriteTheLoanStore } = documentFormStore;
  const { taskV2WithComments } = underwriteTheLoanStore;
  const classes = useStyles();

  useEffect(() => {
    underwriteTheLoanStore.reset();
    underwriteTheLoanStore.loadTasksWithCommentsByLoanId(loanId);
  }, [loanId]);

  const getNotesFromTasks = (tasks: TaskV2[]): TaskNameNotePair[] => {
    return tasks.reduce<TaskNameNotePair[]>((acc, task) => {
      if (task.taskNotes) {
        task.taskNotes.forEach((note) => {
          acc.push({
            taskTitle: task.title,
            taskNote: note,
          });
        });
      }

      return acc;
    }, []);
  }

  const notes = useMemo(() => getNotesFromTasks(taskV2WithComments), [taskV2WithComments]);

  const getTitleName = (taskName: string) => {
    const loanIdPref = `${loanId} -`;
    return taskName.replace(loanIdPref, '');
  }

  return (
    <Box display="flex" flexDirection="column" height="100%" width="100%">
      <Grid container spacing={2} >
        <Grid item xs={12}>
          <Card className={classes.cardWithoutHeader} title={null}>
            {notes.length > 0 ? (
              <>
                {notes.map((item, index) =>
                  <Box key={`${item.taskTitle}-${index}`} className={classes.uwCommentWrapper}>
                    <Typography className={classes.uwDetailsLabel}>{getTitleName(item.taskTitle)}</Typography>
                    <Typography className={classes.uwCommentText}>{item.taskNote.comment}</Typography>
                    <Typography className={classes.uwDetailsLabel}>{`${item.taskNote.commentByUsername} | ${formatDate(item.taskNote.commentDate, DateFormat.MMDDYYYY)}`}</Typography>
                  </Box>
                )}
              </>
            ) : (
              <>
                <Box className={classes.uwCommentWrapper}>
                  <Typography className={classes.uwCommentText}>There are no notes for this task.</Typography>
                </Box>
              </>
            )}
          </Card>
        </Grid>
        <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            onClick={handleClose}
            color="primary"
            variant="outlined"
            testId="confirm"
          >
            Close
          </Button>
        </Grid>
      </Grid>
      {/* button to close */}
    </Box >
  )
});
